import { useAsync } from 'react-use';
import { useTranslation } from 'react-i18next';
import DealerTitle from 'components/Dealer/DealerTitle';
import SectionHeader from 'components/Shared/SectionHeader';
import DealerContact from 'components/Dealer/DealerContact';
import DealerListings from 'components/Dealer/DealerListings';
import About from 'components/Dealer/About';
import { useRef } from 'react';
import Reviews from 'components/Dealer/Reviews';
import GridRow from 'components/Dealer/GridRow';
import NearbyDealers from 'components/Dealer/NearbyDealers';
import * as _ from 'lodash-es';
import { DealerListingsResponse, fetchDealerListings } from 'lib/apis/DealerPageApi';
import { DealerPage } from 'lib/models/DealerPage';
import styles from './styles';

interface DealerProps {
  dealerPage: DealerPage;
}

export default function Dealer({ dealerPage }: DealerProps): JSX.Element {
  const { t } = useTranslation();
  const { dealer, description, phone, email, url, mapUrl, reviews } = dealerPage;

  const { providerName, remoteId } = dealer;
  const nearbyDealersRef = useRef<HTMLDivElement>(null);

  const { value: listingsData } = useAsync(async (): Promise<
    DealerListingsResponse | undefined
  > => {
    if (!remoteId || !providerName) {
      return undefined;
    }

    return fetchDealerListings(remoteId, providerName);
  }, [providerName, remoteId]);

  function onExpand(): void {
    if (nearbyDealersRef.current) {
      window.scrollTo(0, nearbyDealersRef.current.offsetTop);
    }
  }

  const showDealerListings = remoteId && providerName && listingsData && listingsData.total > 0;
  return (
    <div className="dealer-page">
      <GridRow>
        <DealerTitle title={dealer.name} rating={dealer.averageOverallStars} />
      </GridRow>
      <GridRow>
        <DealerContact
          name={dealer.displayName}
          address={dealer.address}
          phone={phone}
          email={email}
          dealerPageUrl={url}
          website={dealer.website}
          websiteName={dealer.websiteName}
          hours={dealer.hours}
          mapUrl={mapUrl}
        />
      </GridRow>
      <SectionHeader>{t('dealerPages.aboutSectionHeader')}</SectionHeader>
      <GridRow>
        <About dealer={dealer} description={description} />
      </GridRow>
      <SectionHeader id="reviews">{t('dealerPages.reviews')}</SectionHeader>
      <GridRow>
        <Reviews reviews={reviews} />
      </GridRow>
      {showDealerListings && (
        <>
          <SectionHeader id="listings">{t('dealerPages.featuredListings')}</SectionHeader>
          <GridRow>
            <DealerListings
              listings={listingsData.records}
              total={listingsData.total}
              providerName={providerName}
              remoteId={remoteId}
            />
          </GridRow>
        </>
      )}
      {!_.isEmpty(dealer.nearbyDealers) && (
        <>
          <div ref={nearbyDealersRef}>
            <SectionHeader>{t('dealerPages.nearbyDealersSectionHeader')}</SectionHeader>
          </div>
          <GridRow>
            <NearbyDealers nearbyDealers={dealer.nearbyDealers} onExpand={onExpand} />
          </GridRow>
        </>
      )}
      <style jsx>{styles}</style>
    </div>
  );
}
